import type { Itinerary, UiLang, Stop } from "./types";
import { dictionaries } from "./i18n";

/**
 * Varsler brukeren når planen endrer seg mens turen pågår.
 *
 * Hva dette *er*: appen sammenligner et nytt værvarsel mot det planen ble laget
 * på, og sier fra når et utsiktspunkt går fra sol til tåke eller omvendt, og
 * når nordlyset faktisk står klart i natt. Varslene er lokale — de sendes av
 * nettleseren på brukerens egen maskin, mens appen er åpen eller ligger
 * installert i bakgrunnen.
 *
 * Hva dette *ikke* er: serverpush. Ekte Web Push krever tre ting appen ikke
 * har: VAPID-nøkler som hemmelighet, et lager for abonnementene (KV eller D1),
 * og en cron-trigger som våkner uten at noen er inne. Alt er gratis hos
 * Cloudflare, men det må settes opp på kontoen — og hele prosjektet er bygget
 * på at det kan deployes og forkes uten nøkler eller ekstra ressurser. Se
 * README for hva som må til hvis man vil ha det.
 *
 * `push`-lytteren i service workeren er likevel på plass, så serverhalvdelen er
 * et tillegg og ikke en omskriving den dagen noen vil ha den.
 *
 * Grensen er sagt rett ut i UI-et. Et varsel man tror våkner en lukket
 * telefon, men som ikke gjør det, er verre enn ingen varsling: da planlegger
 * man etter noe som ikke kommer.
 */

/** Hvor ofte varselet sjekkes på nytt. MET oppdaterer omtrent hver time. */
export const CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * Utsiktsavhengigheten som må til før et værskifte er verdt et varsel.
 *
 * Under dette betyr været lite for opplevelsen: at det er tåke i Bergen sentrum
 * er en detalj, mens tåke på Stegastein er hele turen dit.
 */
const VIEW_DEPENDENCY_FLOOR = 0.7;

/** Hvor mange poeng utsiktsindeksen må flytte seg for at det er en nyhet. */
const SCORE_SHIFT = 18;

/** Nordlyssjansen som må til før det er verdt å vekke noen om kvelden. */
const AURORA_FLOOR = 0.4;

export type NotifyKind = "viewImproved" | "viewLost" | "aurora";

export interface PlanChange {
  kind: NotifyKind;
  stopId: string;
  stopName: string;
  dayNumber: number;
  /** Utsiktsindeksen før og etter, eller nordlyssjansen i prosent. */
  before?: number;
  after: number;
}

/** Alle stopp i planen, med dagnummeret sitt. */
function stopsWithDay(itinerary: Itinerary): { stop: Stop; dayNumber: number }[] {
  return itinerary.days.flatMap((day) => day.stops.map((stop) => ({ stop, dayNumber: day.dayNumber })));
}

/**
 * Sammenligner en ny plan mot den forrige og finner det som er verdt et varsel.
 *
 * Ren funksjon, uten nettverk og uten nettleser-API — hele vurderingen av «er
 * dette en nyhet» kan derfor prøves i test. Det er nødvendig: terskler er
 * nettopp det som blir feil, og en varsling som fyrer for ofte slås av.
 */
export function findChanges(previous: Itinerary, next: Itinerary): PlanChange[] {
  const before = new Map(stopsWithDay(previous).map(({ stop }) => [stop.id, stop]));
  const changes: PlanChange[] = [];

  for (const { stop, dayNumber } of stopsWithDay(next)) {
    const old = before.get(stop.id);

    /*
     * Værskifte på et sted der utsikten er poenget.
     *
     * Begge veier: at Stegastein har klarnet er like nyttig som at det har
     * tåknet — den ene lar deg flytte dagen dit, den andre vekk.
     */
    if (
      old &&
      stop.viewDependency >= VIEW_DEPENDENCY_FLOOR &&
      old.viewScore &&
      stop.viewScore
    ) {
      const shift = stop.viewScore.score - old.viewScore.score;
      if (Math.abs(shift) >= SCORE_SHIFT) {
        changes.push({
          kind: shift > 0 ? "viewImproved" : "viewLost",
          stopId: stop.id,
          stopName: stop.name,
          dayNumber,
          before: old.viewScore.score,
          after: stop.viewScore.score,
        });
      }
    }

    /*
     * Nordlys: bare når alle tre delene stemmer, og bare når det er en endring.
     * `limitedBy` er null nettopp når verken lys, skyer eller aktivitet står i
     * veien — det er tjenestens egen måte å si «i natt går det».
     */
    if (
      stop.aurora &&
      stop.aurora.limitedBy === null &&
      stop.aurora.chance >= AURORA_FLOOR &&
      (old?.aurora?.chance ?? 0) < AURORA_FLOOR
    ) {
      changes.push({
        kind: "aurora",
        stopId: stop.id,
        stopName: stop.name,
        dayNumber,
        after: Math.round(stop.aurora.chance * 100),
      });
    }
  }

  return changes;
}

/** Teksten et varsel viser, på brukerens språk. */
export function notificationFor(change: PlanChange, lang: UiLang): { title: string; body: string } {
  const t = dictionaries[lang];

  switch (change.kind) {
    case "viewImproved":
      return {
        title: t.notifyViewImprovedTitle(change.stopName),
        body: t.notifyViewImprovedBody(change.dayNumber, change.before ?? 0, change.after),
      };
    case "viewLost":
      return {
        title: t.notifyViewLostTitle(change.stopName),
        body: t.notifyViewLostBody(change.dayNumber, change.before ?? 0, change.after),
      };
    case "aurora":
      return {
        title: t.notifyAuroraTitle(change.stopName),
        body: t.notifyAuroraBody(change.after),
      };
  }
}

/** Om nettleseren i det hele tatt kan varsle. Safari på iOS kan bare som PWA. */
export function canNotify(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function notificationsAllowed(): boolean {
  return canNotify() && Notification.permission === "granted";
}

/**
 * Ber om tillatelse. Kalles bare fra et klikk — nettlesere avviser
 * forespørsler som ikke kommer av en handling brukeren gjorde selv, og noen
 * straffer siden for å prøve.
 */
export async function requestPermission(): Promise<boolean> {
  if (!canNotify()) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;

  try {
    return (await Notification.requestPermission()) === "granted";
  } catch {
    return false;
  }
}

/**
 * Viser varslene.
 *
 * Gjennom service workeren der den finnes: et varsel som eies av workeren
 * overlever at fanen lukkes, og kan klikkes tilbake til appen. Uten den faller
 * vi til et vanlig `Notification`, som forsvinner med fanen.
 *
 * `tag` er stopp-id-en, så et nytt varsel om samme sted erstatter det gamle
 * framfor å stable seg opp. Fem varsler om Stegastein er ingen varsling.
 */
export async function showChanges(changes: PlanChange[], lang: UiLang): Promise<void> {
  if (!notificationsAllowed() || changes.length === 0) return;

  const registration = await navigator.serviceWorker?.getRegistration().catch(() => undefined);

  for (const change of changes) {
    const { title, body } = notificationFor(change, lang);
    const options: NotificationOptions = {
      body,
      tag: `${change.kind}:${change.stopId}`,
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      lang,
    };

    if (registration) await registration.showNotification(title, options);
    else new Notification(title, options);
  }
}
